(() => {
  const sectionSelector = '.abrsm-judgeme-strip';
  const pendingSections = new Set();
  let sectionObserver;

  if (window.__abrsmJudgeMeReviewStrip) {
    window.__abrsmJudgeMeReviewStrip.initAll();
    return;
  }

  const collectSections = (root = document) => {
    const sections = [];
    if (root.matches?.(sectionSelector)) sections.push(root);
    root.querySelectorAll?.(sectionSelector).forEach((section) => sections.push(section));
    return sections;
  };

  const initSection = (section) => {
    if (section.dataset.abrsmReviewStripInitialized === 'true') return;
    sectionObserver?.unobserve(section);
    pendingSections.delete(section);
    delete section.dataset.abrsmReviewStripPending;
    section.dataset.abrsmReviewStripInitialized = 'true';

    const details = section.querySelector('[data-abrsm-review-details]');
    const panel = section.querySelector('.abrsm-judgeme-strip__panel');
    const reviewWidget = section.querySelector('.abrsm-judgeme-strip__review-widget');
    const openers = () => Array.from(document.querySelectorAll('[data-abrsm-review-opener]'));
    const imageLoadHandlers = [];
    const initialTimeouts = [];
    let resizeFrame;
    let resizeTimeout;
    let focusTimeout;
    let mutationObserver;
    let carouselHeight = 0;
    let carouselIndex = 0;

    const syncOpeners = () => {
      const expanded = details && details.open ? 'true' : 'false';
      openers().forEach((opener) => {
        opener.setAttribute('aria-expanded', expanded);
        if (panel && panel.id) opener.setAttribute('aria-controls', panel.id);
      });
    };

    const prepareReviewBodyToggles = () => {
      const bodies = Array.from(reviewWidget?.querySelectorAll('.jdgm-carousel-item__review-body') || []);
      bodies.forEach((body) => {
        if (body.dataset.abrsmReviewToggleReady === 'true') return;

        body.classList.add('abrsm-judgeme-strip__review-body');
        body.dataset.abrsmReviewToggleReady = 'true';

        window.requestAnimationFrame(() => {
          const reviewTextLength = (body.textContent || '').trim().length;
          const isOverflowing = body.scrollHeight > body.clientHeight + 2;
          if (!isOverflowing && reviewTextLength < 180) return;

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'abrsm-judgeme-strip__read-toggle';
          button.setAttribute('aria-expanded', 'false');
          button.textContent = 'Read more';

          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const isExpanded = body.classList.toggle('is-expanded');
            body.closest('.jdgm-carousel-item')?.classList.toggle('is-expanded-review', isExpanded);
            button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
            button.textContent = isExpanded ? 'Read less' : 'Read more';
            queueCarouselResize();
          });

          body.insertAdjacentElement('afterend', button);
        });
      });
    };

    const getCarouselParts = () => {
      const container = reviewWidget?.querySelector('.jdgm-carousel__item-container');
      const wrapper = reviewWidget?.querySelector('.jdgm-carousel__item-wrapper');
      const cards = Array.from(reviewWidget?.querySelectorAll('.jdgm-carousel-item') || []);
      return { container, wrapper, cards };
    };

    const getVisibleCardCount = () => {
      const width = reviewWidget?.getBoundingClientRect().width || window.innerWidth;
      if (width >= 960) return 4;
      if (width >= 620) return 2;
      return 1;
    };

    const applyCarouselPosition = () => {
      const { wrapper, cards } = getCarouselParts();
      if (!wrapper || !cards.length) return;

      const visibleCount = getVisibleCardCount();
      const maxIndex = Math.max(cards.length - visibleCount, 0);
      carouselIndex = Math.max(0, Math.min(carouselIndex, maxIndex));

      const cardRect = cards[0].getBoundingClientRect();
      const wrapperStyles = window.getComputedStyle(wrapper);
      const gap = Number.parseFloat(wrapperStyles.columnGap || wrapperStyles.gap || 0) || 0;
      const offset = Math.round(carouselIndex * (cardRect.width + gap));
      wrapper.style.setProperty('transform', `translate3d(${-offset}px, 0, 0)`, 'important');
    };

    const resizeCarousel = () => {
      applyCarouselPosition();
      const { container, wrapper, cards } = getCarouselParts();
      if (!container || !cards.length) return;

      const containerRect = container.getBoundingClientRect();
      const visibleCards = cards.filter((card) => {
        const rect = card.getBoundingClientRect();
        const overlap = Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left);
        return overlap > Math.min(rect.width * 0.45, 140);
      });
      const measuredCards = visibleCards.length ? visibleCards : cards.slice(0, 1);
      const maxHeight = Math.max(...measuredCards.map((card) => Math.ceil(card.getBoundingClientRect().height)));

      if (maxHeight > 0 && Math.abs(maxHeight - carouselHeight) > 1) {
        carouselHeight = maxHeight;
        container.style.setProperty('height', `${maxHeight + 2}px`, 'important');
        wrapper?.style.setProperty('height', 'auto', 'important');
      }
    };

    const handleCarouselArrowClick = (event) => {
      const arrow = event.target.closest('.jdgm-carousel__left-arrow, .jdgm-carousel__right-arrow');
      if (!arrow || !reviewWidget?.contains(arrow)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      const visibleCount = getVisibleCardCount();
      carouselIndex += arrow.classList.contains('jdgm-carousel__right-arrow') ? visibleCount : -visibleCount;
      applyCarouselPosition();
      resizeCarousel();
    };

    const queueCarouselResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      window.clearTimeout(resizeTimeout);
      resizeFrame = window.requestAnimationFrame(() => {
        prepareReviewBodyToggles();
        resizeCarousel();
        resizeTimeout = window.setTimeout(resizeCarousel, 350);
      });
    };

    const openPanel = (event) => {
      if (!details) return;
      event.preventDefault();
      details.open = true;
      syncOpeners();
      queueCarouselResize();
      (panel || section).scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.clearTimeout(focusTimeout);
      focusTimeout = window.setTimeout(() => {
        panel?.focus?.({ preventScroll: true });
      }, 450);
    };

    const handleDetailsToggle = () => {
      syncOpeners();
      queueCarouselResize();
    };

    details?.addEventListener('toggle', handleDetailsToggle);
    reviewWidget?.addEventListener('click', handleCarouselArrowClick, true);
    reviewWidget?.addEventListener('click', queueCarouselResize);
    reviewWidget?.addEventListener('transitionend', queueCarouselResize);
    window.addEventListener('resize', queueCarouselResize, { passive: true });
    reviewWidget?.querySelectorAll('img').forEach((image) => {
      const handleImageLoad = () => queueCarouselResize();
      imageLoadHandlers.push({ image, handleImageLoad });
      image.addEventListener('load', handleImageLoad, { once: true });
    });
    if (reviewWidget) {
      mutationObserver = new MutationObserver(queueCarouselResize);
      mutationObserver.observe(reviewWidget, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    syncOpeners();
    queueCarouselResize();
    initialTimeouts.push(window.setTimeout(queueCarouselResize, 800));
    initialTimeouts.push(window.setTimeout(queueCarouselResize, 1800));
    section.abrsmReviewStripOpenPanel = openPanel;

    section.abrsmReviewStripCleanup = () => {
      window.cancelAnimationFrame(resizeFrame);
      window.clearTimeout(resizeTimeout);
      window.clearTimeout(focusTimeout);
      initialTimeouts.forEach((timeout) => window.clearTimeout(timeout));
      mutationObserver?.disconnect();
      details?.removeEventListener('toggle', handleDetailsToggle);
      reviewWidget?.removeEventListener('click', handleCarouselArrowClick, true);
      reviewWidget?.removeEventListener('click', queueCarouselResize);
      reviewWidget?.removeEventListener('transitionend', queueCarouselResize);
      window.removeEventListener('resize', queueCarouselResize);
      imageLoadHandlers.forEach(({ image, handleImageLoad }) => {
        image.removeEventListener('load', handleImageLoad);
      });
      delete section.dataset.abrsmReviewStripInitialized;
      delete section.abrsmReviewStripOpenPanel;
      delete section.abrsmReviewStripCleanup;
    };
  };

  const observeSection = (section) => {
    if (section.dataset.abrsmReviewStripInitialized === 'true' || pendingSections.has(section)) return;

    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
      initSection(section);
      return;
    }

    if (!sectionObserver) {
      sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) initSection(entry.target);
          });
        },
        { rootMargin: '700px 0px', threshold: 0.01 }
      );
    }

    section.dataset.abrsmReviewStripPending = 'true';
    pendingSections.add(section);
    sectionObserver.observe(section);
  };

  const initAll = (root = document) => {
    collectSections(root).forEach(observeSection);
  };

  const handleReviewOpener = (event) => {
    const opener = event.target.closest('[data-abrsm-review-opener]');
    if (!opener) return;

    const section = collectSections()[0];
    if (!section) return;

    initSection(section);
    section.abrsmReviewStripOpenPanel?.(event);
  };

  window.__abrsmJudgeMeReviewStrip = { initAll };
  document.addEventListener('click', handleReviewOpener);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    collectSections(event.target).forEach(initSection);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    collectSections(event.target).forEach((section) => {
      sectionObserver?.unobserve(section);
      pendingSections.delete(section);
      section.abrsmReviewStripCleanup?.();
    });
  });
})();
